import React from 'react';
import CodeBlock from '../components/CodeBlock';

function SectionHeading({ id, children }) {
  return (
    <h2
      id={id}
      className="font-heading text-2xl font-semibold mt-16 mb-4 scroll-mt-20"
      style={{ color: 'var(--text-primary)' }}
    >
      {children}
    </h2>
  );
}

function Paragraph({ children }) {
  return (
    <p className="leading-relaxed mb-4 font-body" style={{ color: 'var(--text-secondary)' }}>
      {children}
    </p>
  );
}

function EndpointHeader({ method, path }) {
  const badge = `badge-${method.toLowerCase()}`;
  return (
    <div className="flex items-center gap-3 mb-4 mt-8">
      <span className={badge}>{method}</span>
      <code className="font-mono" style={{ fontSize: '15px', color: 'var(--text-secondary)' }}>{path}</code>
    </div>
  );
}

function ParamTable({ params }) {
  return (
    <div className="glass-card overflow-hidden my-4">
      <table className="param-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Type</th>
            <th>Required</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          {params.map((p) => (
            <tr key={p.name}>
              <td className="font-mono text-xs" style={{ color: 'var(--accent-primary)', opacity: 0.8 }}>{p.name}</td>
              <td className="font-mono text-xs" style={{ color: 'var(--text-muted)' }}>{p.type}</td>
              <td>
                {p.required ? (
                  <span className="text-xs font-semibold" style={{ color: 'var(--accent-warning)' }}>Yes</span>
                ) : (
                  <span className="text-xs" style={{ color: 'var(--text-muted)' }}>No</span>
                )}
              </td>
              <td className="text-xs" style={{ color: 'var(--text-secondary)' }}>{p.description}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StatusCodes({ codes }) {
  const colorMap = {
    '200': 'var(--accent-success)', '201': 'var(--accent-success)', '204': 'var(--accent-success)',
    '400': 'var(--accent-warning)', '401': 'var(--accent-danger)', '403': 'var(--accent-danger)',
    '404': 'var(--accent-warning)', '409': 'var(--accent-warning)', '413': 'var(--accent-warning)',
    '422': 'var(--accent-warning)', '429': 'var(--accent-warning)', '500': 'var(--accent-danger)',
  };
  return (
    <div className="glass-card overflow-hidden my-4">
      <table className="param-table">
        <thead>
          <tr><th>Status</th><th>Description</th></tr>
        </thead>
        <tbody>
          {codes.map((c) => (
            <tr key={c.code}>
              <td
                className="font-mono text-xs font-semibold"
                style={{ color: colorMap[c.code] || 'var(--text-secondary)' }}
              >
                {c.code}
              </td>
              <td className="text-xs" style={{ color: 'var(--text-secondary)' }}>{c.description}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function EvidenceAPI() {
  return (
    <article>
      <div className="mb-12">
        <p
          className="text-xs font-mono uppercase tracking-widest mb-2"
          style={{ color: 'var(--accent-primary)', opacity: 0.6 }}
        >
          Evidence API
        </p>
        <h1
          className="font-heading font-bold mb-4"
          style={{ fontSize: '32px', fontWeight: 700, color: 'var(--text-primary)' }}
        >
          Evidence Management
        </h1>
        <Paragraph>
          Upload, query, and track digital evidence with full chain of custody.
          All evidence is integrity-hashed on ingest and stored with immutable
          metadata for forensic-grade traceability.
        </Paragraph>
      </div>

      {/* ── Upload ── */}
      <SectionHeading id="upload">Upload Evidence</SectionHeading>
      <Paragraph>
        Upload a file as evidence. The system automatically computes SHA-256 and
        BLAKE3 hashes, extracts metadata, and creates the initial chain of
        custody record. Maximum file size is 500 MB.
      </Paragraph>

      <EndpointHeader method="POST" path="/api/v1/evidence" />

      <ParamTable
        params={[
          { name: 'file', type: 'binary', required: true, description: 'The evidence file (multipart/form-data).' },
          { name: 'case_id', type: 'string', required: true, description: 'UUID of the associated case.' },
          { name: 'evidence_type', type: 'string', required: true, description: 'One of: image, video, audio, document, archive.' },
          { name: 'description', type: 'string', required: false, description: 'Human-readable description of the evidence.' },
          { name: 'tags', type: 'string[]', required: false, description: 'Array of searchable tags.' },
          { name: 'classification', type: 'string', required: false, description: 'Security classification: public, internal, confidential, restricted.' },
          { name: 'source_device', type: 'string', required: false, description: 'Identifier of the originating device or system.' },
        ]}
      />

      <CodeBlock
        title="Request"
        examples={{
          curl: `curl -X POST https://api.noyka.ai/api/v1/evidence \\
  -H "Authorization: Bearer eyJhbGciOi..." \\
  -F "file=@/path/to/photo.jpg" \\
  -F "case_id=550e8400-e29b-41d4-a716-446655440000" \\
  -F "evidence_type=image" \\
  -F "description=Crime scene photo - north entrance" \\
  -F 'tags=["crime-scene","entrance","photo"]' \\
  -F "classification=confidential"`,
          python: `import requests

with open("/path/to/photo.jpg", "rb") as f:
    response = requests.post(
        "https://api.noyka.ai/api/v1/evidence",
        headers={"Authorization": "Bearer eyJhbGciOi..."},
        files={"file": ("photo.jpg", f, "image/jpeg")},
        data={
            "case_id": "550e8400-e29b-41d4-a716-446655440000",
            "evidence_type": "image",
            "description": "Crime scene photo - north entrance",
            "tags": '["crime-scene","entrance","photo"]',
            "classification": "confidential",
        },
    )
evidence = response.json()`,
          javascript: `const formData = new FormData();
formData.append("file", fileInput.files[0]);
formData.append("case_id", "550e8400-e29b-41d4-a716-446655440000");
formData.append("evidence_type", "image");
formData.append("description", "Crime scene photo - north entrance");
formData.append("tags", JSON.stringify(["crime-scene", "entrance", "photo"]));
formData.append("classification", "confidential");

const response = await fetch("https://api.noyka.ai/api/v1/evidence", {
  method: "POST",
  headers: { Authorization: "Bearer eyJhbGciOi..." },
  body: formData,
});
const evidence = await response.json();`,
          go: `body := &bytes.Buffer{}
writer := multipart.NewWriter(body)
part, _ := writer.CreateFormFile("file", "photo.jpg")
io.Copy(part, file)
writer.WriteField("case_id", "550e8400-e29b-41d4-a716-446655440000")
writer.WriteField("evidence_type", "image")
writer.WriteField("description", "Crime scene photo - north entrance")
writer.WriteField("classification", "confidential")
writer.Close()

req, _ := http.NewRequest("POST", "https://api.noyka.ai/api/v1/evidence", body)
req.Header.Set("Authorization", "Bearer eyJhbGciOi...")
req.Header.Set("Content-Type", writer.FormDataContentType())
resp, err := http.DefaultClient.Do(req)`,
        }}
      />

      <CodeBlock
        title="Response 201"
        examples={{
          json: `{
  "id": "ev_8f3a2b1c4d5e6789",
  "case_id": "550e8400-e29b-41d4-a716-446655440000",
  "filename": "photo.jpg",
  "evidence_type": "image",
  "mime_type": "image/jpeg",
  "size_bytes": 2458901,
  "description": "Crime scene photo - north entrance",
  "tags": ["crime-scene", "entrance", "photo"],
  "classification": "confidential",
  "hashes": {
    "sha256": "a7ffc6f8bf1ed76651c14756a061d662f580ff4de43b49fa82d80a4b80f8434a",
    "blake3": "3b7df4c3e8a1f2d0c9b5e6a4d7f1c8e2b0a3d6f9c2e5b8a1d4f7c0e3b6a9d2"
  },
  "chain_of_custody": {
    "created_by": "usr_a1b2c3d4",
    "created_at": "2026-03-13T10:30:00Z",
    "events": 1
  },
  "worm_record_id": "worm_9e8d7c6b5a4f3210",
  "created_at": "2026-03-13T10:30:00Z"
}`,
        }}
      />

      <StatusCodes
        codes={[
          { code: '201', description: 'Evidence uploaded and processed successfully.' },
          { code: '400', description: 'Invalid file format or missing required fields.' },
          { code: '401', description: 'Invalid or expired authentication token.' },
          { code: '404', description: 'Referenced case not found.' },
        ]}
      />

      {/* ── Query ── */}
      <SectionHeading id="query">Query Evidence</SectionHeading>
      <Paragraph>
        Search and filter evidence with powerful query parameters. Results are
        paginated and sorted by creation date (newest first) by default.
      </Paragraph>

      <EndpointHeader method="GET" path="/api/v1/evidence" />

      <ParamTable
        params={[
          { name: 'case_id', type: 'string', required: false, description: 'Filter by case UUID.' },
          { name: 'evidence_type', type: 'string', required: false, description: 'Filter by type: image, video, audio, document, archive.' },
          { name: 'classification', type: 'string', required: false, description: 'Filter by classification level.' },
          { name: 'tags', type: 'string', required: false, description: 'Comma-separated tag filter (AND logic).' },
          { name: 'q', type: 'string', required: false, description: 'Full-text search across description and metadata.' },
          { name: 'created_after', type: 'ISO 8601', required: false, description: 'Return evidence created after this timestamp.' },
          { name: 'created_before', type: 'ISO 8601', required: false, description: 'Return evidence created before this timestamp.' },
          { name: 'page', type: 'integer', required: false, description: 'Page number (default: 1).' },
          { name: 'per_page', type: 'integer', required: false, description: 'Items per page, max 100 (default: 25).' },
          { name: 'sort', type: 'string', required: false, description: 'Sort field: created_at, size_bytes, filename.' },
          { name: 'order', type: 'string', required: false, description: 'Sort order: asc or desc (default: desc).' },
        ]}
      />

      <CodeBlock
        title="Request"
        examples={{
          curl: `curl "https://api.noyka.ai/api/v1/evidence?case_id=550e8400-e29b-41d4-a716-446655440000&evidence_type=image&per_page=10" \\
  -H "Authorization: Bearer eyJhbGciOi..."`,
          python: `response = requests.get(
    "https://api.noyka.ai/api/v1/evidence",
    headers={"Authorization": "Bearer eyJhbGciOi..."},
    params={
        "case_id": "550e8400-e29b-41d4-a716-446655440000",
        "evidence_type": "image",
        "per_page": 10,
    },
)
results = response.json()`,
          javascript: `const params = new URLSearchParams({
  case_id: "550e8400-e29b-41d4-a716-446655440000",
  evidence_type: "image",
  per_page: "10",
});
const response = await fetch(
  \`https://api.noyka.ai/api/v1/evidence?\${params}\`,
  { headers: { Authorization: "Bearer eyJhbGciOi..." } }
);
const results = await response.json();`,
          go: `req, _ := http.NewRequest("GET", "https://api.noyka.ai/api/v1/evidence", nil)
q := req.URL.Query()
q.Add("case_id", "550e8400-e29b-41d4-a716-446655440000")
q.Add("evidence_type", "image")
q.Add("per_page", "10")
req.URL.RawQuery = q.Encode()
req.Header.Set("Authorization", "Bearer eyJhbGciOi...")
resp, err := http.DefaultClient.Do(req)`,
        }}
      />

      <CodeBlock
        title="Response 200"
        examples={{
          json: `{
  "data": [
    {
      "id": "ev_8f3a2b1c4d5e6789",
      "case_id": "550e8400-e29b-41d4-a716-446655440000",
      "filename": "photo.jpg",
      "evidence_type": "image",
      "size_bytes": 2458901,
      "classification": "confidential",
      "created_at": "2026-03-13T10:30:00Z"
    }
  ],
  "pagination": {
    "page": 1,
    "per_page": 10,
    "total_items": 47,
    "total_pages": 5
  }
}`,
        }}
      />

      <StatusCodes
        codes={[
          { code: '200', description: 'Query executed successfully.' },
          { code: '400', description: 'Invalid query parameters.' },
          { code: '401', description: 'Invalid or expired authentication token.' },
          { code: '404', description: 'Referenced case not found.' },
        ]}
      />

      {/* ── Chain of Custody ── */}
      <SectionHeading id="chain-of-custody">Chain of Custody</SectionHeading>
      <Paragraph>
        Retrieve the full chain of custody for a specific piece of evidence.
        Every access, transfer, and modification is recorded as an immutable
        event with timestamp, actor, and action metadata.
      </Paragraph>

      <EndpointHeader method="GET" path="/api/v1/evidence/{id}/chain" />

      <ParamTable
        params={[
          { name: 'id', type: 'string', required: true, description: 'Evidence ID (path parameter).' },
          { name: 'from', type: 'ISO 8601', required: false, description: 'Filter events after this timestamp.' },
          { name: 'to', type: 'ISO 8601', required: false, description: 'Filter events before this timestamp.' },
          { name: 'action', type: 'string', required: false, description: 'Filter by action: created, accessed, transferred, exported, verified.' },
        ]}
      />

      <CodeBlock
        title="Request"
        examples={{
          curl: `curl "https://api.noyka.ai/api/v1/evidence/ev_8f3a2b1c4d5e6789/chain" \\
  -H "Authorization: Bearer eyJhbGciOi..."`,
          python: `response = requests.get(
    "https://api.noyka.ai/api/v1/evidence/ev_8f3a2b1c4d5e6789/chain",
    headers={"Authorization": "Bearer eyJhbGciOi..."},
)
chain = response.json()`,
          javascript: `const response = await fetch(
  "https://api.noyka.ai/api/v1/evidence/ev_8f3a2b1c4d5e6789/chain",
  { headers: { Authorization: "Bearer eyJhbGciOi..." } }
);
const chain = await response.json();`,
          go: `req, _ := http.NewRequest("GET",
    "https://api.noyka.ai/api/v1/evidence/ev_8f3a2b1c4d5e6789/chain", nil)
req.Header.Set("Authorization", "Bearer eyJhbGciOi...")
resp, err := http.DefaultClient.Do(req)`,
        }}
      />

      <CodeBlock
        title="Response 200"
        examples={{
          json: `{
  "evidence_id": "ev_8f3a2b1c4d5e6789",
  "integrity_status": "verified",
  "current_hash": "a7ffc6f8bf1ed76651c14756a061d662f580ff4de43b49fa82d80a4b80f8434a",
  "events": [
    {
      "id": "coc_001",
      "action": "created",
      "actor": "usr_a1b2c3d4",
      "actor_role": "investigator",
      "timestamp": "2026-03-13T10:30:00Z",
      "ip_address": "198.51.100.42",
      "details": {
        "method": "api_upload",
        "source_device": "field-laptop-07"
      }
    },
    {
      "id": "coc_002",
      "action": "verified",
      "actor": "system",
      "actor_role": "worm_engine",
      "timestamp": "2026-03-13T10:30:02Z",
      "details": {
        "verification": "hash_match",
        "worm_record_id": "worm_9e8d7c6b5a4f3210"
      }
    },
    {
      "id": "coc_003",
      "action": "accessed",
      "actor": "usr_e5f6g7h8",
      "actor_role": "analyst",
      "timestamp": "2026-03-13T14:15:00Z",
      "ip_address": "203.0.113.88",
      "details": {
        "access_type": "view",
        "duration_seconds": 45
      }
    }
  ],
  "total_events": 3
}`,
        }}
      />

      <StatusCodes
        codes={[
          { code: '200', description: 'Chain of custody retrieved successfully.' },
          { code: '400', description: 'Invalid query parameters.' },
          { code: '401', description: 'Invalid or expired authentication token.' },
          { code: '404', description: 'Evidence not found.' },
        ]}
      />
    </article>
  );
}
